/*
 * Copyright (c) 2025 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:mta-learning":true});}catch(e){}}();import Player from '@vimeo/player'
import { Loader2 } from 'lucide-react'
import Image from 'next/image'
import React, { useCallback, useState } from 'react'

import { GenericDialog } from '@/components/ui/GenericDialog'
import { VimeoMetadata } from '@/lib/vimeo/metadata'
import { cn } from '@/utils/css'

/** A description of the available properties for this component. */
type VimeoModalPlayerProps = {
  /** The Vimeo video ID to play. */
  videoId: string

  /** A function to call when the player is closed. */
  onClose: () => void

  /** The Vimeo video metadata, containing the thumbnail URL. */
  vimeoMetadata: VimeoMetadata
}

/**
 * A modal wrapper around the Vimeo player. The video will autoplay when the modal is opened. The maximum width of the
 * player is 800px, or the window width minus 64px, whichever is smaller (to enable responsive design).
 */
const VimeoModalPlayer: React.FC<VimeoModalPlayerProps> = ({ videoId, onClose, vimeoMetadata }) => {
  const [loading, setLoading] = useState(true)

  const playerCallback = useCallback(
    (node: HTMLDivElement | null) => {
      if (!node) return

      const width = Math.min(window.innerWidth - 64, 800)
      const player = new Player(node, {
        id: Number(videoId),
        width,
        loop: false,
        autoplay: true,
      })

      player.on('loaded', () => {
        setLoading(false)
      })
    },
    [videoId],
  )

  return (
    <GenericDialog open={true} handleClose={onClose} title={vimeoMetadata.title} className={'p-3'}>
      <div className={'mt-3 relative aspect-video w-full max-w-[800px] overflow-hidden rounded-md'}>
        <div
          className={cn(
            'absolute inset-0 flex items-center justify-center bg-black transition-opacity duration-300',
            !loading && 'opacity-0',
          )}
        >
          <Image
            src={vimeoMetadata.thumbnail_large}
            alt={''}
            className={'w-full h-full object-cover rounded-md opacity-50'}
            fill
            priority
          />
          <div className={'absolute'}>
            <Loader2 className={'animate-spin text-white'} size={32} />
          </div>
        </div>
        <div
          ref={playerCallback}
          className={cn('relative w-full h-full transition-opacity duration-300', loading && 'opacity-0')}
        />
      </div>
    </GenericDialog>
  )
}

export { VimeoModalPlayer }
